import React, { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation, Link } from 'react-router-dom';
import { AlertTriangle, Lock, CreditCard, ArrowRight } from 'lucide-react';

/**
 * iter 261 — BillingHoldGuard
 *
 * Renders one of three UI states based on `user.billing_status`:
 *
 *  1. **No billing issue** → renders `children` untouched (fast path, zero cost).
 *  2. **Grace period** (`payment_failed_at` set, `on_hold` false) → renders
 *     children with a persistent yellow banner at top explaining the deadline.
 *  3. **On hold** (`on_hold: true`) → allows only whitelisted routes
 *     (dispatch dashboard, messaging, broker dashboard, billing settings).
 *     For any other route, renders a full-page "Payment required" screen
 *     with an "Update payment" CTA. The user can still use the whitelisted
 *     routes without seeing the block screen.
 *
 * Backend `require_module` mirrors this logic server-side, so even a request
 * that bypasses the guard cannot reach paid modules while on hold.
 */

// Routes that stay usable while on hold. Match prefixes to allow /dashboard/foo etc.
const HOLD_ALLOWED_PREFIXES = [
  '/dashboard',
  '/messaging',
  '/broker',
  '/broker-dashboard',
  '/settings',       // needs to be here so admin can pay
  '/pricing',
  '/subscription',
  '/logout',
  '/super-admin',    // super admin bypass
];

const isAllowedPath = (pathname) => {
  if (!pathname) return true;
  return HOLD_ALLOWED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`) || pathname.startsWith(`${p}?`));
};

const daysUntil = (isoTs) => {
  if (!isoTs) return null;
  const target = new Date(isoTs).getTime();
  if (Number.isNaN(target)) return null;
  const diffMs = target - Date.now();
  return Math.max(0, Math.ceil(diffMs / (24 * 60 * 60 * 1000)));
};

const GraceBanner = ({ daysLeft }) => (
  <div
    className="w-full bg-amber-500 text-slate-900 px-4 py-2 flex items-center justify-between gap-3 shadow-md"
    style={{ position: 'sticky', top: 0, zIndex: 9998 }}
    data-testid="billing-grace-banner"
  >
    <div className="flex items-center gap-2 text-sm font-medium">
      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
      <span>
        Your monthly payment failed.{' '}
        {daysLeft !== null && daysLeft > 0 ? (
          <>You have <b>{daysLeft} {daysLeft === 1 ? 'day' : 'days'}</b> to update your card before paid modules are put on hold.</>
        ) : (
          <>Please update your card soon to avoid a service interruption.</>
        )}
      </span>
    </div>
    <Link
      to="/settings/billing"
      className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-amber-300 px-3 py-1 rounded text-xs font-semibold"
      data-testid="billing-grace-cta"
    >
      <CreditCard className="h-3.5 w-3.5" />
      Update payment
    </Link>
  </div>
);

const HoldBlockScreen = () => (
  <div
    className="min-h-screen flex items-center justify-center p-6"
    style={{ backgroundColor: '#0f172a' }}
    data-testid="billing-hold-block-screen"
  >
    <div className="max-w-lg w-full rounded-2xl border border-red-500/30 p-8 shadow-2xl" style={{ backgroundColor: '#0b1220' }}>
      <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-red-500/15 mb-5">
        <Lock className="h-3.5 w-3.5 text-red-400" />
        <span className="text-red-300 text-[11px] font-semibold tracking-wider">ACCOUNT ON HOLD</span>
      </div>
      <div className="text-2xl font-bold mb-3" style={{ color: '#ffffff' }}>
        Payment required to restore full access
      </div>
      <div className="text-sm mb-6" style={{ color: '#94a3b8' }}>
        Your monthly payment could not be processed within the 5-day grace period, so paid modules are temporarily suspended.
        You can still use <b style={{ color: '#e2e8f0' }}>Dispatch</b>, <b style={{ color: '#e2e8f0' }}>Messaging</b>, and <b style={{ color: '#e2e8f0' }}>Broker Dashboard</b> while this is resolved.
      </div>
      <Link
        to="/settings/billing"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 font-semibold text-sm shadow-lg shadow-emerald-500/20"
        style={{ color: '#0f172a' }}
        data-testid="billing-hold-update-cta"
      >
        <CreditCard className="h-4 w-4" />
        Update payment method
        <ArrowRight className="h-4 w-4" />
      </Link>
      <div className="mt-4 text-xs" style={{ color: '#64748b' }}>
        Stripe smart retries continue in the background. If a retry succeeds, this block clears automatically.
      </div>
    </div>
  </div>
);

const BillingHoldGuard = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();

  const state = useMemo(() => {
    const bs = user?.billing_status;
    if (!bs) return { kind: 'ok' };
    if (bs.on_hold) return { kind: 'hold' };
    if (bs.payment_failed_at) {
      return { kind: 'grace', daysLeft: daysUntil(bs.hold_scheduled_at) };
    }
    return { kind: 'ok' };
  }, [user?.billing_status]);

  // Super admins always bypass — helps you debug customer accounts without
  // being locked out of your own tooling.
  if (user?.role === 'super_admin') return children;

  if (state.kind === 'ok') return children;

  if (state.kind === 'grace') {
    return (
      <>
        <GraceBanner daysLeft={state.daysLeft} />
        {children}
      </>
    );
  }

  // state.kind === 'hold'
  if (isAllowedPath(location.pathname)) {
    return (
      <>
        <GraceBanner daysLeft={0} />
        {children}
      </>
    );
  }
  return <HoldBlockScreen />;
};

export default BillingHoldGuard;
